import React, { useState, useRef, useEffect } from 'react';
import { X, GripHorizontal } from 'lucide-react';
import { ViewType } from '../../utils/workspace.types';

interface ViewInfoPanelProps {
  currentView: ViewType;
  isVisible: boolean;
  onClose: () => void;
}

/**
 * ViewInfoPanel - Draggable non-modal panel showing view information
 * 
 * Design follows Theme_Format_readme.md:
 * - Blue header (#2563EB) with draggable behavior
 * - Centered on open with smart positioning
 * - White dialog background with consistent spacing
 * - NO backdrop overlay - canvas remains interactive
 * - Focus trap and keyboard support (Escape to close)
 * - Consistent button and text styling
 */
export const ViewInfoPanel: React.FC<ViewInfoPanelProps> = ({
  currentView,
  isVisible,
  onClose,
}) => {
  const DIALOG_WIDTH = 240;  // Reduced from 320 (25% smaller)
  const DIALOG_HEIGHT = 280; // Reduced from 400 (30% smaller)
  
  // Center dialog on first open
  const getInitialPosition = () => ({
    x: Math.max(0, (window.innerWidth - DIALOG_WIDTH) / 2),
    y: Math.max(0, (window.innerHeight - DIALOG_HEIGHT) / 2),
  });
  
  const [position, setPosition] = useState(getInitialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  // Get view information based on current view
  const getViewInfo = () => {
    switch (currentView) {
      case 'front':
        return {
          plane: 'XZ Plane',
          viewName: 'FRONT',
          description: 'Front Elevation',
          axes: [
            { name: 'X-axis', direction: 'Horizontal', color: 'text-red-400' },
            { name: 'Z-axis', direction: 'Vertical (Height)', color: 'text-blue-400' },
            { name: 'Y-axis', direction: 'Depth (Hidden)', color: 'text-gray-500' },
          ],
          cameraPosition: 'South (0, -80, 0)',
          lookingDirection: 'North (+Y)',
        };
      case 'top':
        return {
          plane: 'XY Plane',
          viewName: 'TOP View (PLAN)',
          description: 'Floor Plan',
          axes: [
            { name: 'X-axis', direction: 'Horizontal (Width)', color: 'text-red-400' },
            { name: 'Y-axis', direction: 'Horizontal (Depth)', color: 'text-green-400' },
            { name: 'Z-axis', direction: 'Elevation (Hidden)', color: 'text-gray-500' },
          ],
          cameraPosition: 'Above (0, 0, 80)',
          lookingDirection: 'Down (-Z)',
        };
      case 'side':
        return {
          plane: 'YZ Plane',
          viewName: 'SIDE View (RIGHT)',
          description: 'Right Elevation',
          axes: [
            { name: 'Y-axis', direction: 'Horizontal (Depth)', color: 'text-green-400' },
            { name: 'Z-axis', direction: 'Vertical (Height)', color: 'text-blue-400' },
            { name: 'X-axis', direction: 'Width (Hidden)', color: 'text-gray-500' },
          ],
          cameraPosition: 'East (80, 0, 0)',
          lookingDirection: 'West (-X)',
        };
      case 'isometric':
        return {
          plane: 'All Planes',
          viewName: '3D View (ISOMETRIC)',
          description: 'Perspective View',
          axes: [
            { name: 'X-axis', direction: 'East-West', color: 'text-red-400' },
            { name: 'Y-axis', direction: 'North-South', color: 'text-green-400' },
            { name: 'Z-axis', direction: 'Up-Down', color: 'text-blue-400' },
          ],
          cameraPosition: 'SE Corner (50, -50, 50)',
          lookingDirection: 'To Origin',
        };
      default:
        return {
          plane: 'Unknown',
          viewName: 'Unknown View',
          description: '',
          axes: [],
          cameraPosition: '',
          lookingDirection: '',
        };
    }
  };

  const viewInfo = getViewInfo();

  // Handle drag start - only on header, not on close button
  const handleMouseDown = (e: React.MouseEvent) => {
    // Exclude close button from drag detection
    const target = e.target as HTMLElement;
    if (target.closest('button')) {
      return;
    }
    
    // Only start drag from header
    if (!target.classList.contains('drag-handle') && !target.closest('.drag-handle')) {
      return;
    }
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
    
    // Disable text selection during drag
    document.body.style.userSelect = 'none';
  };

  // Handle dragging with constraints
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Calculate new position
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      // Constrain to viewport - keep 100px visible at edges
      const constrainedX = Math.max(0, Math.min(window.innerWidth - DIALOG_WIDTH, newX));
      const constrainedY = Math.max(0, Math.min(window.innerHeight - 100, newY));

      setPosition({ x: constrainedX, y: constrainedY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    };
  }, [isDragging, dragStart, position]);

  // Update position on window resize (keep dialog accessible)
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => ({
        x: Math.min(prev.x, window.innerWidth - DIALOG_WIDTH),
        y: Math.min(prev.y, window.innerHeight - 100),
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Focus trap and keyboard support
  useEffect(() => {
    if (!isVisible || !panelRef.current) return;

    // Focus the panel when it opens
    panelRef.current.focus();

    // Handle Escape key to close
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      className="fixed bg-white rounded-lg shadow-xl z-50 pointer-events-auto"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${DIALOG_WIDTH}px`,
      }}
    >
        {/* Draggable blue header */}
        <div
          className="drag-handle bg-blue-600 text-white px-3 py-2 rounded-t-lg cursor-grab active:cursor-grabbing select-none flex items-center justify-between"
          onMouseDown={handleMouseDown}
        >
          <div className="drag-handle flex items-center space-x-2 pointer-events-none">
            <GripHorizontal className="w-3 h-3" />
            <span className="text-sm font-semibold">View Info</span>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 p-1 rounded transition-colors"
            title="Close panel"
            aria-label="Close view information panel"
          >
            <X className="w-3 h-3" />
          </button>
        </div>

        {/* Content with compact spacing (p-3) */}
        <div className="p-3 space-y-2">
          {/* Current View */}
          <div className="p-2 bg-gray-50 rounded">
            <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Current View</div>
            <div className="text-base font-semibold text-gray-900">{viewInfo.viewName}</div>
            <div className="text-xs text-gray-700 mt-0.5">{viewInfo.description}</div>
          </div>

          {/* Working Plane */}
          <div className="p-2 bg-gray-50 rounded">
            <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Working Plane</div>
            <div className="text-xs font-medium text-blue-600">{viewInfo.plane}</div>
          </div>

          {/* Axis Information */}
          <div className="p-2 bg-gray-50 rounded">
            <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Axes</div>
            <div className="space-y-1">
              {viewInfo.axes.map((axis, index) => (
                <div key={index} className="flex items-center justify-between text-xs">
                  <span className={`font-medium ${axis.color}`}>{axis.name}</span>
                  <span className="text-gray-700">{axis.direction}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Camera Information */}
          <div className="p-2 bg-gray-50 rounded">
            <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Camera</div>
            <div className="space-y-0.5 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-700">Position:</span>
                <span className="text-gray-900 font-medium">{viewInfo.cameraPosition}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Looking:</span>
                <span className="text-gray-900 font-medium">{viewInfo.lookingDirection}</span>
              </div>
            </div>
          </div>

          {/* Coordinate System Note */}
          <div className="text-[10px] text-gray-500 italic text-center pt-1 border-t border-gray-300">
            Z-UP System
          </div>
        </div>
    </div>
  );
};
